import React from 'react';
import { Alert, AlertTitle, Button } from '@mui/material';

/**
 * ErrorState - an error the reader can act on (design.md §5.5).
 *
 * Always offers a way forward when one exists, and never surfaces a raw backend
 * message. Callers pass a sentence written for the reader; keep the technical
 * detail in the console.
 *
 * Props:
 *  - error    : string or Error; falls back to a generic sentence
 *  - title    : optional AlertTitle
 *  - onRetry  : shows a Retry action
 *  - retryLabel : defaults to 'Retry'
 *  - onDismiss  : shows the Alert close button
 *  - action   : extra node rendered after Retry
 *  - severity : defaults to 'error'
 */
const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

const ErrorState = ({
  error,
  title,
  onRetry,
  retryLabel = 'Retry',
  onDismiss,
  action,
  severity = 'error',
  sx,
}) => {
  if (!error && !title) return null;

  const message =
    (typeof error === 'string' && error) ||
    error?.message ||
    DEFAULT_MESSAGE;

  return (
    <Alert
      severity={severity}
      onClose={onDismiss}
      sx={{ mb: 3, ...sx }}
      action={(onRetry || action) ? (
        <>
          {onRetry && (
            <Button color="inherit" size="small" onClick={onRetry}>
              {retryLabel}
            </Button>
          )}
          {action}
        </>
      ) : undefined}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {message}
    </Alert>
  );
};

export default ErrorState;
